import { useRef, useState, type DragEvent } from 'react';

/**
 * Files dragged in from the desktop or a file manager. Only drags that carry
 * files count — moving a picture between plate cells is a drag too — and only
 * those over this element's own DOM: React bubbles events out of portals, so a
 * dialog opened from here would otherwise report its drags as ours.
 */
function isOwnFileDrag(event: DragEvent<HTMLElement>): boolean {
    return (
        Array.from(event.dataTransfer.types).includes('Files') &&
        event.currentTarget.contains(event.target as Node)
    );
}

/**
 * A drop zone for files. `active` while files hover over it; spread
 * `dropProps` onto the element that takes them.
 *
 * Every child entered fires its own dragenter/dragleave pair, so a counter —
 * not the last event — decides whether the drag is still inside.
 */
export function useFileDrop(
    enabled: boolean,
    onFiles: (files: FileList) => void
) {
    const [active, setActive] = useState(false);
    const depth = useRef(0);

    if (!enabled) {
        return { active: false, dropProps: {} };
    }

    return {
        active,
        dropProps: {
            onDragEnter(event: DragEvent<HTMLElement>) {
                if (!isOwnFileDrag(event)) {
                    return;
                }
                event.preventDefault();
                depth.current += 1;
                setActive(true);
            },
            onDragOver(event: DragEvent<HTMLElement>) {
                if (!isOwnFileDrag(event)) {
                    return;
                }
                // Without this the browser opens the file in place of the app.
                event.preventDefault();
                event.dataTransfer.dropEffect = 'copy';
            },
            onDragLeave(event: DragEvent<HTMLElement>) {
                if (!isOwnFileDrag(event)) {
                    return;
                }
                depth.current = Math.max(0, depth.current - 1);
                if (depth.current === 0) {
                    setActive(false);
                }
            },
            onDrop(event: DragEvent<HTMLElement>) {
                if (!isOwnFileDrag(event)) {
                    return;
                }
                event.preventDefault();
                depth.current = 0;
                setActive(false);
                if (event.dataTransfer.files.length > 0) {
                    onFiles(event.dataTransfer.files);
                }
            }
        }
    };
}
